import { BaseApiService } from './base.service';
import type { Job, QueueAggregate } from '@/features/design/job-queue';
import type { RenderBlockReason } from '@/types/render';

interface QueueStatusResponse {
  data: {
    type: 'queue_status';
    id: string;
    attributes: {
      queue_limit: number;
      active_renders: number;
      available_slots: number;
      can_render: boolean;
      block_reason: RenderBlockReason;
      renders: {
        pending: number;
        processing: number;
      };
    };
  };
}

/**
 * JSON:API format response from /v1/history
 * Returns unified job-render and job-album types
 */
export interface JobHistoryResponse {
  data: Array<{
    type: 'job-render' | 'job-album';
    id: string;
    attributes: {
      status: string;
      created_at: string | null;
      started_at: string | null;
      completed_at: string | null;
      duration_predicted: number | null;
      // Render-specific fields
      image_url?: string | null;
      is_upscaled?: boolean;
      settings?: Record<string, unknown>;
      // Album-specific fields
      template?: string;
      // Whether blueprints were bundled (server-driven, replaces full/short).
      include_blueprints?: boolean;
      format?: string;
      language?: string;
      filename?: string;
      file_size?: string;
      public_url?: string;
    };
    relationships?: {
      concept?: {
        data: { type: string; id: string } | null;
        meta?: { name: string | null; project_sequence_number: number | null };
      };
      project?: {
        data: { type: string; id: string } | null;
        meta?: { name: string | null };
      };
      concept_angle?: {
        data: { type: string; id: string } | null;
        meta?: { is_main: boolean | null; angle_id: string | null };
      };
    };
  }>;
}

export class JobQueueService extends BaseApiService {
  /**
   * Get unified job queue with limit using /v1/history endpoint
   * Returns the last N jobs (renders + PDF albums) for the authenticated user
   * Now returns typed Job discriminated union for type safety
   */
  async getQueue(limit: number = 10): Promise<Job[]> {
    const response = await this.httpGet<JobHistoryResponse>(
      `/v1/history?page[size]=${limit}&page[number]=1&sort=-created_at`
    );

    return response.data.map((item): Job => {
      const attrs = item.attributes;
      const rels = item.relationships;

      // Common fields for all job types
      const baseJob = {
        id: item.id,
        status: attrs.status as
          | 'pending'
          | 'processing'
          | 'completed'
          | 'failed',
        startedAt: attrs.started_at,
        durationPredicted: attrs.duration_predicted ?? 90,
        attemptNumber: 1,
        createdAt: attrs.created_at ?? new Date().toISOString(),
        updatedAt: attrs.created_at ?? new Date().toISOString(),
        completedAt: attrs.completed_at,
        conceptId: rels?.concept?.data?.id ?? '',
        projectId: rels?.project?.data?.id ?? '',
        conceptName: rels?.concept?.meta?.name ?? null,
        conceptSequenceNumber:
          rels?.concept?.meta?.project_sequence_number ?? 0,
        projectName: rels?.project?.meta?.name ?? '',
      };

      // Discriminate based on type
      if (item.type === 'job-album') {
        return {
          ...baseJob,
          jobType: 'album',
          conceptAngleId: null,
          angleType: null,
          albumTemplate: attrs.template ?? '',
          albumIncludeBlueprints: attrs.include_blueprints ?? false,
          albumFormat: attrs.format ?? '',
          albumLanguage: attrs.language ?? '',
          albumFilename: attrs.filename ?? null,
          albumPublicUrl: attrs.public_url ?? null,
        };
      } else {
        // job-render - check if upscaled
        const isUpscaled = attrs.is_upscaled ?? false;
        const isMainAngle = rels?.concept_angle?.meta?.is_main ?? true;
        const conceptAngleId = rels?.concept_angle?.data?.id ?? '';

        if (isUpscaled) {
          return {
            ...baseJob,
            jobType: 'upscale',
            scenarioType: 'upscale',
            conceptAngleId,
            angleType: isMainAngle ? 'main' : 'derivative',
            originalRenderId: item.id, // For upscaled renders, the ID is the original
            imageUrl: attrs.image_url ?? null,
            angleUrl: null,
          };
        } else {
          return {
            ...baseJob,
            jobType: 'render',
            scenarioType: undefined,
            conceptAngleId,
            angleType: isMainAngle ? 'main' : 'derivative',
            imageUrl: attrs.image_url ?? null,
            angleUrl: null,
            isUpscaled: false,
          };
        }
      }
    });
  }

  /**
   * Cold-start fetch of the server-authoritative render-slot aggregate.
   * Live updates flow via WS queue:snapshot after the first connection lands.
   */
  async getQueueStatus(): Promise<QueueAggregate> {
    const response = await this.httpGet<QueueStatusResponse>('/v1/queue/status');
    const attrs = response.data.attributes;
    return {
      queueLimit: attrs.queue_limit,
      activeRenders: attrs.active_renders,
      availableSlots: attrs.available_slots,
      canRender: attrs.can_render,
      blockReason: attrs.block_reason ?? null,
    };
  }
}

export const jobQueueService = new JobQueueService();
